import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Filter, Grid3X3, List, MapPin, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Layout } from '../components/layout/Layout';
import { FilterSidebar } from '../components/tours/FilterSidebar';
import { SearchBar } from '../components/tours/SearchBar';
import { SortOptions } from '../components/tours/SortOptions';
import { PackageGrid } from '../components/tours/PackageGrid';
import { Pagination } from '../components/tours/Pagination';
import { EnquiryModal } from '../components/tours/EnquiryModal';
import { publicApi } from '../lib/api';
import { cn } from '../lib/utils';

const ITEMS_PER_PAGE = 10;

export default function ToursPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Tours data
  const [tours, setTours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  // UI State
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [viewMode, setViewMode] = useState('grid'); // grid or list
  const [enquiryTour, setEnquiryTour] = useState(null);

  // Filters
  const [filters, setFilters] = useState({
    search: searchParams.get('destination') || '',
    categories: searchParams.get('category') ? [searchParams.get('category')] : [],
    minPrice: parseInt(searchParams.get('min_price')) || 0,
    maxPrice: parseInt(searchParams.get('max_price')) || 500000,
    minDuration: parseInt(searchParams.get('min_duration')) || null,
    maxDuration: parseInt(searchParams.get('max_duration')) || null,
    durationId: null,
  });

  // Sort and Pagination
  const [sortId, setSortId] = useState('popular');
  const [sortBy, setSortBy] = useState('views');
  const [sortOrder, setSortOrder] = useState('desc');
  const [currentPage, setCurrentPage] = useState(parseInt(searchParams.get('page')) || 1);

  // Load tours from API
  const loadTours = useCallback(async () => {
    try {
      setLoading(true);
      
      const params = {
        page: currentPage,
        limit: ITEMS_PER_PAGE,
        sort_by: sortBy,
        sort_order: sortOrder,
      };

      // Add search/destination filter
      if (filters.search) {
        params.destination = filters.search;
      }

      // Add category filter (use first selected category for API)
      if (filters.categories && filters.categories.length > 0) {
        params.category = filters.categories[0];
      }

      // Add price filters
      if (filters.minPrice > 0) {
        params.min_price = filters.minPrice;
      }
      if (filters.maxPrice < 500000) {
        params.max_price = filters.maxPrice;
      }

      // Add duration filters
      if (filters.minDuration) {
        params.min_duration = filters.minDuration;
      }
      if (filters.maxDuration) {
        params.max_duration = filters.maxDuration;
      }

      const response = await publicApi.getTours(params);
      
      // Handle paginated response
      if (response.data && typeof response.data === 'object') {
        if (Array.isArray(response.data)) {
          setTours(response.data);
          setTotalCount(response.data.length);
          setTotalPages(Math.ceil(response.data.length / ITEMS_PER_PAGE));
        } else {
          setTours(response.data.data || []);
          setTotalCount(response.data.total || 0);
          setTotalPages(response.data.total_pages || 1);
        }
      }
    } catch (error) {
      console.error('Error loading tours:', error);
      setTours([]);
    } finally {
      setLoading(false);
    }
  }, [currentPage, sortBy, sortOrder, filters]);

  // Initial load and when filters change
  useEffect(() => {
    loadTours();
  }, [loadTours]);

  // Update URL params
  useEffect(() => {
    const params = new URLSearchParams();
    
    if (filters.search) params.set('destination', filters.search);
    if (filters.categories.length > 0) params.set('category', filters.categories[0]);
    if (filters.minPrice > 0) params.set('min_price', filters.minPrice.toString());
    if (filters.maxPrice < 500000) params.set('max_price', filters.maxPrice.toString());
    if (filters.minDuration) params.set('min_duration', filters.minDuration.toString());
    if (filters.maxDuration) params.set('max_duration', filters.maxDuration.toString());
    if (currentPage > 1) params.set('page', currentPage.toString());

    setSearchParams(params);
  }, [filters, currentPage, setSearchParams]);

  // Handle filter changes
  const handleFilterChange = (newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
    setCurrentPage(1); // Reset to first page on filter change
  };

  // Handle search
  const handleSearch = () => {
    setCurrentPage(1);
    loadTours();
  };

  // Handle sort change
  const handleSortChange = (id, newSortBy, newSortOrder) => {
    setSortId(id);
    setSortBy(newSortBy);
    setSortOrder(newSortOrder);
    setCurrentPage(1);
  };

  // Handle page change
  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      search: '',
      categories: [],
      minPrice: 0,
      maxPrice: 500000,
      minDuration: null,
      maxDuration: null,
      durationId: null,
    });
    setSortId('popular');
    setSortBy('views');
    setSortOrder('desc');
    setCurrentPage(1);
  };

  // Handle enquiry
  const handleEnquire = (tour) => {
    setEnquiryTour(tour);
  };

  // Count active filters
  const activeFilterCount = [
    filters.categories.length > 0,
    filters.minPrice > 0 || filters.maxPrice < 500000,
    filters.durationId,
  ].filter(Boolean).length;

  return (
    <Layout>
      {/* Hero Header */}
      <section className="relative bg-gradient-to-r from-[#004E64] to-[#006B87] py-16 md:py-20 overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-32 h-32 bg-white rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-48 h-48 bg-[#FF6B35] rounded-full blur-3xl" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4">
          <div className="max-w-2xl">
            <span className="inline-block text-[#FF6B35] text-sm font-semibold tracking-wider uppercase mb-3">
              ✈️ Explore Tours
            </span>
            <h1 
              className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Find Your Perfect Journey
            </h1>
            <p className="text-white/80 text-lg">
              Discover handcrafted travel experiences designed to create unforgettable memories 
              across stunning destinations worldwide.
            </p>
          </div>

          {/* Search Bar in Hero */}
          <div className="mt-8 max-w-2xl">
            <SearchBar
              value={filters.search}
              onChange={(value) => handleFilterChange({ search: value })}
              onSearch={handleSearch}
            />
          </div>

          {/* Quick Stats */}
          <div className="flex flex-wrap gap-6 mt-8 text-white/80">
            <div className="flex items-center gap-2">
              <MapPin className="w-5 h-5" />
              <span>50+ Destinations</span>
            </div>
            <div className="flex items-center gap-2">
              <span>500+ Tour Packages</span>
            </div>
            <div className="flex items-center gap-2">
              <span>15K+ Happy Travelers</span>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-8 md:py-12 bg-[#FDFBF7]">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex gap-8">
            {/* Desktop Filter Sidebar */}
            <aside className="hidden lg:block w-72 flex-shrink-0">
              <FilterSidebar
                filters={filters}
                onFilterChange={handleFilterChange}
                onClearFilters={clearFilters}
              />
            </aside>

            {/* Main Content Area */}
            <div className="flex-1 min-w-0">
              {/* Toolbar */}
              <div className="bg-white rounded-2xl p-4 shadow-sm mb-6 border border-gray-100">
                <div className="flex flex-wrap items-center justify-between gap-4">
                  {/* Results Count & Mobile Filter Button */}
                  <div className="flex items-center gap-4">
                    {/* Mobile Filter Button */}
                    <Button
                      variant="outline"
                      onClick={() => setShowMobileFilters(true)}
                      className="lg:hidden h-10 px-4 rounded-xl border-gray-200 relative"
                    >
                      <Filter className="w-4 h-4 mr-2" />
                      Filters
                      {activeFilterCount > 0 && (
                        <Badge className="absolute -top-2 -right-2 bg-[#FF6B35] text-white text-xs h-5 w-5 p-0 flex items-center justify-center rounded-full">
                          {activeFilterCount}
                        </Badge>
                      )}
                    </Button>

                    {/* Results Count */}
                    <div className="text-sm text-[#4A4A4A]">
                      {loading ? (
                        <span className="flex items-center gap-2">
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Loading...
                        </span>
                      ) : (
                        <>
                          Showing <span className="font-semibold text-[#1A1A1A]">{tours.length}</span> of{' '}
                          <span className="font-semibold text-[#1A1A1A]">{totalCount}</span> tours
                        </>
                      )}
                    </div>
                  </div>

                  {/* Sort & View Options */}
                  <div className="flex items-center gap-3">
                    {/* Sort Dropdown */}
                    <SortOptions
                      value={sortId}
                      onChange={handleSortChange}
                    />

                    {/* View Toggle */}
                    <div className="hidden sm:flex items-center gap-1 bg-gray-100 rounded-lg p-1">
                      <button
                        onClick={() => setViewMode('grid')}
                        className={cn(
                          'p-2 rounded-md transition-colors',
                          viewMode === 'grid'
                            ? 'bg-white shadow-sm text-[#FF6B35]'
                            : 'text-[#4A4A4A] hover:text-[#1A1A1A]'
                        )}
                      >
                        <Grid3X3 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setViewMode('list')}
                        className={cn(
                          'p-2 rounded-md transition-colors',
                          viewMode === 'list'
                            ? 'bg-white shadow-sm text-[#FF6B35]'
                            : 'text-[#4A4A4A] hover:text-[#1A1A1A]'
                        )}
                      >
                        <List className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Active Filters Tags */}
                {(filters.categories.length > 0 || filters.durationId || filters.search) && (
                  <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-gray-100">
                    {filters.search && (
                      <Badge
                        variant="secondary"
                        className="bg-[#FF6B35]/10 text-[#FF6B35] hover:bg-[#FF6B35]/20 cursor-pointer"
                        onClick={() => handleFilterChange({ search: '' })}
                      >
                        📍 {filters.search} ×
                      </Badge>
                    )}
                    {filters.categories.map(cat => (
                      <Badge
                        key={cat}
                        variant="secondary"
                        className="bg-[#004E64]/10 text-[#004E64] hover:bg-[#004E64]/20 cursor-pointer"
                        onClick={() => handleFilterChange({ 
                          categories: filters.categories.filter(c => c !== cat) 
                        })}
                      >
                        {cat} ×
                      </Badge>
                    ))}
                    {filters.durationId && (
                      <Badge
                        variant="secondary"
                        className="bg-[#2A9D8F]/10 text-[#2A9D8F] hover:bg-[#2A9D8F]/20 cursor-pointer"
                        onClick={() => handleFilterChange({ 
                          durationId: null, 
                          minDuration: null, 
                          maxDuration: null 
                        })}
                      >
                        {filters.durationId} Days ×
                      </Badge>
                    )}
                  </div>
                )}
              </div>

              {/* Tours Grid */}
              <PackageGrid
                tours={tours}
                loading={loading}
                onEnquire={handleEnquire}
                onClearFilters={clearFilters}
                columns={viewMode === 'list' ? 2 : 3}
              />

              {/* Pagination */}
              {!loading && totalPages > 1 && (
                <div className="mt-8">
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Mobile Filter Drawer */}
      <FilterSidebar
        filters={filters}
        onFilterChange={handleFilterChange}
        onClearFilters={clearFilters}
        isOpen={showMobileFilters}
        onClose={() => setShowMobileFilters(false)}
        isMobile={true}
      />

      {/* Enquiry Modal */}
      <EnquiryModal
        isOpen={!!enquiryTour}
        onClose={() => setEnquiryTour(null)}
        tour={enquiryTour}
      />
    </Layout>
  );
}
